import React, { useEffect, useState } from "react";
import axiosBase from "axios";
import Layout from "../../Component/Layout";
import Select from "react-select";
import { FaTrashAlt } from 'react-icons/fa';
import { hasPermission } from "../../utils/permission";
import { toast } from "react-toastify";
import { MASTER_API } from "../../apiConfig";

// Sends the login token on every request (the backend now guards this module with verifyToken).
const axios = axiosBase.create();
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});


const selectStyles = (disabled) => ({
  control: (base) => ({
    ...base,
    minHeight: 40,
    fontSize: 13,
    backgroundColor: disabled ? "#f1f5f9" : base.backgroundColor,
    cursor: disabled ? "not-allowed" : "default",
  }),
  singleValue: (base) => ({ ...base, color: disabled ? "#475569" : base.color }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
});
const smallInput = { fontSize: "13px", padding: "6px 10px", height: "36px", borderRadius: "4px", border: "1px solid #ced4da", width: "100%", outline: "none" };
const smallLabel = { fontSize: "13px", fontWeight: 600, color: '#334155', marginBottom: "6px", display: "block" };
const thStyle = { padding: "12px 15px", borderBottom: "1px solid #ccc", textAlign: "left", whiteSpace: "nowrap", color: "#334155", fontWeight: "600" };
const tdStyle = { padding: "12px 15px", borderBottom: "1px solid #eee", color: "#475569" };


const RoutingConfig = () => {
  const canView = hasPermission("MD_ROUTING_CONFIG", "view");
  const canCreate = hasPermission("MD_ROUTING_CONFIG", "create");
  const canEdit = hasPermission("MD_ROUTING_CONFIG", "edit");


  const [configList, setConfigList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);


  const [processFilter, setProcessFilter] = useState(null);
  const [subprocessFilter, setSubprocessFilter] = useState(null);


  const [form, setForm] = useState({
    process_id: "",
    process: "",
    sub_process_id: "",
    subprocess: ""
  });


  const [parameters, setParameters] = useState([{ name: "", uom: "" }]);
  const [routingLogs, setRoutingLogs] = useState([]);
  const [activeTab, setActiveTab] = useState("parameters");
  const [processMaster, setProcessMaster] = useState([]);
  const [subProcessMaster, setSubProcessMaster] = useState([]);
  const [processSubProcess, setProcessSubProcess] = useState([]);
  const [uomOptions, setUomOptions] = useState([]);


  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;


  // ── Bulk upload / drafts state ──
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showMasterPreview, setShowMasterPreview] = useState(false);
  const [draftsList, setDraftsList] = useState([]);
  const [draftsCount, setDraftsCount] = useState(0);
  const [previewRoutingNumber, setPreviewRoutingNumber] = useState(null);


  useEffect(() => {
    loadConfigList();
    loadProcessSubProcess();
    loadUomList();
    loadDrafts();
  }, []);


  const loadConfigList = async () => {
  try {
    const res = await axios.get(`${MASTER_API}/routing-configuration`, {
      headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" },
      params: { _t: Date.now() }
    });
    setConfigList(res.data.data || res.data || []);
  } catch (err) {
    console.error("Failed to load Routing Configurations", err);
    setConfigList([]);
  }
};


  const loadProcessSubProcess = async () => {
    try {
      const res = await axios.get(`${MASTER_API}/configuration/dynamic/process-configuration`);
      setProcessSubProcess(res.data || []);
    } catch (err) {
      console.error("Failed to load Process Subprocess", err);
      setProcessSubProcess([]);
    }
  };


  const loadUomList = async () => {
    try {
      const res = await axios.get(`${MASTER_API}/configuration/uom`);
      const options = (res.data.data || res.data || []).map(uom => ({
        value: uom.name,
        label: uom.name
      }));
      setUomOptions(options);
    } catch (err) {
      console.error("Failed to load UOM list", err);
    }
  };


  const loadDrafts = async () => {
  try {
    const res = await axios.get(`${MASTER_API}/routing-configuration/get-drafts`, {
      headers: { "Cache-Control": "no-cache", "Pragma": "no-cache" },
      params: { _t: Date.now() } // forces a unique URL every call, browser can't 304 it
    });
    if (res.data.success) {
      setDraftsList(res.data.drafts || []);
      setDraftsCount((res.data.drafts || []).length);
    }
  } catch (err) {
    console.error("Failed to load drafts", err);
  }
};


  const handleBulkExcelUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;
  const formData = new FormData();
  formData.append("file", file);
  try {
    const res = await axios.post(`${MASTER_API}/routing-configuration/parse-excel`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
    if (res.data.success) {
      setShowBulkModal(false);
      await loadDrafts();        // ✅ wait for state to update first
      setShowMasterPreview(true);
      if (res.data.message) toast.info(res.data.message); // surface backend's actual outcome
    }
  } catch (err) {
    toast.error("Failed to parse the file. Ensure the template is correct.");
  }
  event.target.value = null;
};


  const handlePublishDraft = async (routingNumber) => {
    if (!hasPermission("MD_ROUTING_CONFIG", "approve") && false) {
      // keep placeholder in case an approve permission gets added later
    }
    try {
      await axios.post(`${MASTER_API}/routing-configuration/publish-draft`, { routingNumber });
      toast.success("Routing Configuration saved successfully!");
      loadDrafts();
      loadConfigList();
      if (draftsList.length <= 1) setShowMasterPreview(false);
    } catch (err) {
      toast.error("Failed to save Routing Configuration.");
    }
  };


  const handlePublishAllDrafts = async () => {
    try {
      await axios.post(`${MASTER_API}/routing-configuration/publish-all-drafts`);
      toast.success("All Routing Configurations saved successfully!");
      loadDrafts();
      loadConfigList();
      setShowMasterPreview(false);
    } catch (err) {
      toast.error("Failed to save Routing Configurations.");
    }
  };


  const viewPreviewDetails = (draft) => {
  setEditingId("preview");
  setPreviewRoutingNumber(draft.routing_number);   // ✅ track which draft this is
  setForm({
    process_id: draft.process_id || "",
    process: draft.process || "",
    sub_process_id: draft.sub_process_id || "",
    subprocess: draft.subprocess || ""
  });
  setParameters(draft.parameters?.length ? draft.parameters : [{ name: "", uom: "" }]);
  setRoutingLogs([]);
  setActiveTab("parameters");
  setShowModal(true);
};


  const openCreate = () => {
    setEditingId(null);
    setForm({ process_id: "", process: "", sub_process_id: "", subprocess: "" });
    setParameters([{ name: "", uom: "" }]);
    setRoutingLogs([]);
    setActiveTab("parameters");
    setShowModal(true);
  };


  const viewDetails = async (config) => {
    if (!canView) {
      toast.error("You don't have View access. Please contact admin.");
      return;
    }


    setEditingId(config.id);
    setForm({
      process_id: config.process_id || "",
      process: config.process || "",
      sub_process_id: config.sub_process_id || "",
      subprocess: config.subprocess || ""
    });


    setParameters(config.parameters?.length ? config.parameters : [{ name: "", uom: "" }]);


    try {
      const logRes = await axios.get(`${MASTER_API}/routing-configuration/change-logs/${config.id}`);
      setRoutingLogs(logRes.data || []);
    } catch (err) {
      console.error("Failed to fetch logs", err);
      setRoutingLogs([]);
    }
    setShowModal(true);
  };


  const addParameter = () => {
    if (!canEdit) {
      toast.error("You don't have Edit access. Please contact admin.");
      return;
    }
    if (parameters.length < 30) {
      setParameters([...parameters, { name: "", uom: "" }]);
    } else {
      toast.error("Maximum of 30 parameters allowed.");
    }
  };


  const removeParameter = (index) => {
    if (!canEdit) {
      toast.error("You don't have Edit access. Please contact admin.");
      return;
    }
    const updated = parameters.filter((_, i) => i !== index);
    setParameters(updated.length > 0 ? updated : [{ name: "", uom: "" }]);
  };


  const updateParameter = (index, field, value) => {
    if (!canEdit) {
      toast.error("You don't have Edit access. Please contact admin.");
      return;
    }
    const updated = [...parameters];
    updated[index][field] = value;
    setParameters(updated);
  };


  const handleSave = async () => {
    if (editingId === "preview") {
      if (!canCreate) {
        toast.error("You don't have Create access. Please contact admin.");
        return;
      }
    } else if (!editingId && !canCreate) {
      toast.error("You don't have Create access. Please contact admin.");
      return;
    } else if (editingId && editingId !== "view_only" && !canEdit) {
      toast.error("You don't have Edit access. Please contact admin.");
      return;
    }


    if (!form.process) {
      return toast.error("Please fill in Process.");
    }


    const validParameters = parameters.filter(p => p.name.trim() !== "" || p.uom !== "");
    if (validParameters.length === 0) {
      return toast.error("Please add at least one parameter.");
    }


    const payload = {
      process_id: form.process_id,
      process: form.process,
      sub_process_id: form.sub_process_id,
      subprocess: form.subprocess,
      parameters: validParameters
    };


    try {
      if (editingId === "preview") {
        // ✅ Editing a DRAFT — save back to routing_configuration_drafts, NOT the live table
        await axios.put(`${MASTER_API}/routing-configuration/drafts/${previewRoutingNumber}`, payload);
        toast.success("Draft saved successfully");
        setShowModal(false);
        setPreviewRoutingNumber(null);
        await loadDrafts(); // refreshes both draftsList (behind the modal) and draftsCount badge
      } else if (editingId) {
        await axios.put(`${MASTER_API}/routing-configuration/${editingId}`, payload);
        toast.success("Routing Configuration updated successfully");
        setShowModal(false);
        loadConfigList();
      } else {
        await axios.post(`${MASTER_API}/routing-configuration`, payload);
        toast.success("Routing Configuration created successfully");
        setShowModal(false);
        loadConfigList();
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save Routing Configuration.");
    }
  };


  const handleDelete = async (item) => {
    const canDelete = hasPermission("MD_ROUTING_CONFIG", "delete");
    if (!canDelete) {
      toast.error("You don't have Delete access. Please contact admin.");
      return;
    }
    const confirmed = window.confirm(`Delete "${item.process}" routing configuration?`);
    if (!confirmed) return;
    try {
      await axios.delete(`${MASTER_API}/routing-configuration/${item.id}`);
      toast.success("Routing configuration deleted successfully");
      loadConfigList();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete routing configuration.");
    }
  };

  // Process / Subprocess fields are read-only when viewing an existing configuration
  const isViewMode = !!editingId && editingId !== "preview";

  const processOptions = Array.from(new Set(configList.map(item => item.process))).filter(Boolean).map(p => ({ value: p, label: p }));
  const subprocessOptions = Array.from(new Set(configList.map(item => item.subprocess))).filter(Boolean).map(s => ({ value: s, label: s }));


  const filteredList = configList.filter((item) => {
    const matchProcess = !processFilter || item.process === processFilter.value;
    const matchSubprocess = !subprocessFilter || item.subprocess === subprocessFilter.value;
    return matchProcess && matchSubprocess;
  });


  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedList = filteredList.slice(startIndex, endIndex);


  return (
    <Layout>
      <div style={{ padding: 20 }}>
        {/* HEADER SECTION */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 26, fontWeight: 700, color: '#1e293b' }}>Routing Configuration</div>
            <div style={{ fontSize: 13, color: '#64748b' }}>Define and manage process parameters for Master Data</div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              className="btn"
              style={{ padding: "8px 16px", background: "#fff", color: "#1e293b", border: "1px solid #1e293b", borderRadius: 4, cursor: "pointer", fontWeight: 600 }}
              onClick={() => {
                if (!canCreate) return toast.error("You don't have Create access. Please contact admin.");
                setShowBulkModal(true);
              }}
            >
              ☁️ Create Configuration By Upload
            </button>
            <button
              className="btn btn-dark"
              style={{ padding: "8px 16px", background: "#1e293b", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 600 }}
              onClick={() => {
                if (!canCreate) {
                  toast.error("You don't have Create access. Please contact admin.");
                  return;
                }
                openCreate();
              }}
            >
              + Create Configuration
            </button>
          </div>
        </div>


        {/* FILTERS */}
        <div style={{ background: '#ffffff', borderRadius: 8, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 18 }}>
          <div className="row g-3">
            <div className="col-md-4">
              <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block', color: '#334155' }}>Process</label>
              <Select options={processOptions} value={processFilter} onChange={setProcessFilter} placeholder="Search or Select Process..." isClearable styles={{ control: (base) => ({ ...base, minHeight: 36, fontSize: 13, borderColor: '#ced4da' }), menu: (base) => ({ ...base, zIndex: 9999, fontSize: 13 }) }} />
            </div>
            <div className="col-md-4">
              <label style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, display: 'block', color: '#334155' }}>Subprocess</label>
              <Select options={subprocessOptions} value={subprocessFilter} onChange={setSubprocessFilter} placeholder="Search or Select Subprocess..." isClearable styles={{ control: (base) => ({ ...base, minHeight: 36, fontSize: 13, borderColor: '#ced4da' }), menu: (base) => ({ ...base, zIndex: 9999, fontSize: 13 }) }} />
            </div>
          </div>
        </div>


        {/* DATA TABLE */}
        <div style={{ width: "100%", background: "white", borderRadius: 6, overflowX: "auto", fontSize: "13px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f1f1f1" }}>
                <th style={thStyle}>Process</th>
                <th style={thStyle}>Subprocess</th>
                <th style={thStyle}>Total Parameter</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {filteredList.length === 0 ? (
                <tr><td colSpan="5" style={{ padding: 30, textAlign: "center", color: "#94a3b8", fontSize: 14 }}>No Routing Configurations found</td></tr>
              ) : (
                paginatedList.map((item) => {
                  const paramCount = (item.parameters || []).length;
                  return (
                    <tr key={item.id} style={{ transition: "background 0.2s" }} onMouseOver={e => e.currentTarget.style.background = "#f8fafc"} onMouseOut={e => e.currentTarget.style.background = "white"}>
                      <td style={{ ...tdStyle, fontWeight: 600, color: "#0f172a" }}>{item.process}</td>
                      <td style={tdStyle}>{item.subprocess}</td>
                      <td style={tdStyle}>
                        <span style={{ background: paramCount > 0 ? "#e0f2fe" : "#f1f5f9", color: paramCount > 0 ? "#0284c7" : "#94a3b8", padding: "4px 8px", borderRadius: 12, fontSize: 11, fontWeight: 700 }}>
                          {paramCount} Defined
                        </span>
                      </td>
                      <td style={{ ...tdStyle, display: "flex", gap: 8, alignItems: "center" }}>
                        <button onClick={() => viewDetails(item)} style={{ padding: "6px 12px", background: "#1E88E5", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                          View
                        </button>
                        <button onClick={() => handleDelete(item)} style={{ padding: "6px 12px", background: "#dc3545", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>


          {/* PAGINATION */}
          {filteredList.length > 0 && (
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, padding: "12px 15px" }}>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                style={{ padding: "6px 12px", border: "1px solid #ccc", background: "#fff", cursor: currentPage === 1 ? "not-allowed" : "pointer", borderRadius: 4, fontSize: 14 }}
              >
                {"<"}
              </button>
              <span style={{ fontSize: 13, color: "#334155" }}>Page {currentPage} of {totalPages || 1}</span>
              <button
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage((p) => p + 1)}
                style={{ padding: "6px 12px", border: "1px solid #ccc", background: "#fff", cursor: (currentPage === totalPages || totalPages === 0) ? "not-allowed" : "pointer", borderRadius: 4, fontSize: 14 }}
              >
                {">"}
              </button>
            </div>
          )}
        </div>


        {/* BULK UPLOAD MODAL */}
        {showBulkModal && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 1050, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <div style={{ background: "white", width: "400px", borderRadius: 12, padding: 25, boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                <h4 style={{ margin: 0 }}>Upload Routing Configuration</h4>
                <button onClick={() => setShowBulkModal(false)} style={{ background: "transparent", border: "none", fontSize: 18, cursor: "pointer" }}>✖</button>
              </div>
              <div style={{ border: "2px dashed #cbd5e1", borderRadius: 8, padding: 30, textAlign: "center", background: "#f8fafc" }}>
                <p style={{ margin: "0 0 15px 0", fontSize: 14, color: "#475569" }}>Upload your Routing Configuration Template</p>
                <input type="file" id="routingExcelUpload" accept=".xlsx, .xls, .csv" style={{ display: "none" }} onChange={handleBulkExcelUpload} />
                <label htmlFor="routingExcelUpload" style={{ background: "#03183a", color: "white", padding: "8px 20px", borderRadius: 6, cursor: "pointer", fontWeight: 600, display: "inline-block", marginBottom: 15 }}>Browse File</label>
                <div>
                  <a href="/assets/files/RoutingConfiguration.xlsx" download style={{ fontSize: 13, color: "#03183a", textDecoration: "none", fontWeight: 500 }}>⬇ Download Template</a>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* MASTER PREVIEW TABLE */}
        {showMasterPreview && (
          <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 1060, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <div style={{ background: "white", width: "85vw", height: "85vh", borderRadius: 12, padding: 20, display: "flex", flexDirection: "column", boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #eee", paddingBottom: 15, marginBottom: 15 }}>
                <h3 style={{ margin: 0, color: "#1e293b" }}>👀 Preview Uploaded Routing Configurations</h3>
                <button onClick={() => setShowMasterPreview(false)} style={{ background: "#f1f5f9", border: "none", padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>Close</button>
              </div>


              <div style={{ flex: 1, overflowY: "auto", paddingRight: 10 }}>
                {draftsList.length === 0 ? (
                  <p style={{ textAlign: 'center', marginTop: 40, color: '#666' }}>No Pending Routing Configurations found.</p>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
  <thead>
    <tr style={{ background: "#f1f1f1" }}>
      <th style={thStyle}>Process ID</th>
      <th style={thStyle}>Process</th>
      <th style={thStyle}>Sub Process ID</th>
      <th style={thStyle}>Subprocess</th>
      <th style={thStyle}>Parameters (UOM)</th>
      <th style={thStyle}>Total Parameter</th>
      <th style={thStyle}>Actions</th>
    </tr>
  </thead>
  <tbody>
    {draftsList.map((draft, idx) => (
      <tr key={idx}>
        <td style={tdStyle}>{draft.process_id || "-"}</td>
        <td style={{ ...tdStyle, fontWeight: 600, color: "#0f172a" }}>{draft.process}</td>
        <td style={tdStyle}>{draft.sub_process_id || "-"}</td>
        <td style={tdStyle}>{draft.subprocess || "-"}</td>
        <td style={{ ...tdStyle, maxWidth: 320, whiteSpace: "normal" }}>
          {(draft.parameters || [])
            .map(p => `${p.name}${p.uom ? ` (${p.uom})` : ""}`)
            .join(", ")}
        </td>
        <td style={tdStyle}>
          <span style={{ background: "#e0f2fe", color: "#0284c7", padding: "4px 8px", borderRadius: 12, fontSize: 11, fontWeight: 700 }}>
            {(draft.parameters || []).length} Defined
          </span>
        </td>
        <td style={tdStyle}>
          <button onClick={() => viewPreviewDetails(draft)} style={{ padding: "6px 10px", background: "#1E88E5", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, marginRight: 8 }}>View</button>
          <button onClick={() => handlePublishDraft(draft.routing_number)} style={{ padding: "6px 10px", background: "#10b981", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12 }}>Save</button>
        </td>
      </tr>
    ))}
  </tbody>
</table>
                )}
              </div>
              {draftsList.length > 0 && (
                <div style={{ borderTop: "1px solid #eee", paddingTop: 15, marginTop: 15, display: "flex", justifyContent: "flex-end" }}>
                  <button onClick={handlePublishAllDrafts} style={{ background: "#10b981", color: "white", border: "none", padding: "10px 24px", borderRadius: 6, cursor: "pointer", fontWeight: 600, fontSize: 15 }}>Save All Configurations</button>
                </div>
              )}
            </div>
          </div>
        )}


        {/* FLOATING BADGE */}
        <div style={{ position: 'fixed', bottom: 30, right: 30, display: 'flex', flexDirection: 'column', gap: 15, zIndex: 9999 }}>
          {draftsCount > 0 && (
            <div onClick={() => setShowMasterPreview(true)} style={{ background: '#1e293b', color: '#fff', padding: '12px 20px', borderRadius: 50, cursor: 'pointer', boxShadow: '0 8px 20px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontWeight: 'bold', fontSize: 15 }}>📂 Pending Drafts</span>
              <span style={{ background: '#ef4444', color: 'white', padding: '2px 8px', borderRadius: 20, fontSize: 12, fontWeight: 'bold' }}>{draftsCount}</span>
            </div>
          )}
        </div>


        {/* CREATE / EDIT MODAL */}
        {showModal && (
          <>
            <div onClick={() => setShowModal(false)} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100 }} />
            <div onClick={(e) => e.stopPropagation()} style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "1200px", maxWidth: "95vw", maxHeight: "90vh", display: "flex", flexDirection: "column", background: "white", zIndex: 1101, borderRadius: 8, boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)" }}>


              <div style={{ padding: "18px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0, color: "#1e293b", fontSize: 18, fontWeight: 600 }}>
                  {editingId === "preview" ? "Preview Routing Configuration" : (editingId ? "Routing Configuration" : "Create Routing Configuration")}
                </h3>
                <button onClick={() => setShowModal(false)} style={{ background: "transparent", border: "none", fontSize: 22, cursor: "pointer", color: "#64748b", lineHeight: 1 }}>&times;</button>
              </div>


              <div style={{ padding: "24px", overflowY: "auto", flex: 1, background: "#fafafa" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 24, background: "white", padding: 20, borderRadius: 6, border: "1px solid #e2e8f0" }}>
                  <div>
                    <label style={smallLabel}>Process ID</label>
                    <Select
                      isDisabled={isViewMode}
                      options={[...new Map(processSubProcess.map(p => [p.process_id, { value: p.process_id, label: p.process_id }])).values()]}
                      value={form.process_id ? { value: form.process_id, label: form.process_id } : null}
                      onChange={(s) => { const row = processSubProcess.find(p => p.process_id === s?.value); setForm({ ...form, process_id: row?.process_id || "", process: row?.process_name || "", sub_process_id: "", subprocess: "" }); }}
                      placeholder="Select Process ID"
                      isClearable={!isViewMode}
                      styles={selectStyles(isViewMode)}
                    />
                  </div>
                  <div>
                    <label style={smallLabel}>Process</label>
                    <Select
                      isDisabled={isViewMode}
                      options={[...new Map(processSubProcess.map(p => [p.process_name, { value: p.process_name, label: `${p.process_id} - ${p.process_name}` }])).values()]}
                      value={form.process ? { value: form.process, label: form.process } : null}
                      onChange={(s) => { const row = processSubProcess.find(p => p.process_name === s?.value); setForm({ ...form, process_id: row?.process_id || "", process: row?.process_name || "", sub_process_id: "", subprocess: "" }); }}
                      placeholder="Select Process..."
                      isClearable={!isViewMode}
                      styles={selectStyles(isViewMode)}
                    />
                  </div>
                  <div>
                    <label style={smallLabel}>Sub Process ID</label>
                    <Select
                      isDisabled={isViewMode}
                      options={processSubProcess.filter(x => x.process_name === form.process).map(x => ({ value: x.sub_process_id, label: x.sub_process_id }))}
                      value={form.sub_process_id ? { value: form.sub_process_id, label: form.sub_process_id } : null}
                      onChange={(s) => { const row = processSubProcess.find(x => x.sub_process_id === s?.value); setForm({ ...form, sub_process_id: row?.sub_process_id || "", subprocess: row?.sub_process_name || "" }); }}
                      placeholder="Select Sub Process ID"
                      isClearable={!isViewMode}
                      styles={selectStyles(isViewMode)}
                    />
                  </div>
                  <div>
                    <label style={smallLabel}>Subprocess</label>
                    <Select
                      isDisabled={isViewMode}
                      options={processSubProcess.filter(x => x.process_name === form.process).map(x => ({ value: x.sub_process_name, label: `${x.sub_process_id || ""} - ${x.sub_process_name || ""}` }))}
                      value={form.subprocess ? { value: form.subprocess, label: form.subprocess } : null}
                      onChange={(s) => { const row = processSubProcess.find(x => x.sub_process_name === s?.value); setForm({ ...form, sub_process_id: row?.sub_process_id || "", subprocess: row?.sub_process_name || "" }); }}
                      placeholder="Select Sub Process..."
                      isClearable={!isViewMode}
                      styles={selectStyles(isViewMode)}
                    />
                  </div>
                </div>


                <div style={{ background: "white", padding: 20, borderRadius: 6, border: "1px solid #e2e8f0" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <div style={{ display: "flex", gap: 12 }}>
                      <button onClick={() => setActiveTab("parameters")} style={{ padding: "8px 14px", border: "none", borderBottom: activeTab === "parameters" ? "3px solid #1976d2" : "3px solid transparent", background: "transparent", fontWeight: activeTab === "parameters" ? 700 : 500, cursor: "pointer" }}>Process Parameters</button>
                      <button onClick={() => setActiveTab("changeLog")} style={{ padding: "8px 14px", border: "none", borderBottom: activeTab === "changeLog" ? "3px solid #1976d2" : "3px solid transparent", background: "transparent", fontWeight: activeTab === "changeLog" ? 700 : 500, cursor: "pointer" }}>Change Log</button>
                    </div>
                    <button onClick={addParameter} disabled={parameters.length >= 30} style={{ padding: "6px 14px", background: parameters.length >= 30 ? "#cbd5e1" : "#1e293b", color: "white", border: "none", borderRadius: 4, cursor: parameters.length >= 30 ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600 }}>+ Add Parameter</button>
                  </div>


                  {activeTab === "parameters" && (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                          <th style={{ padding: "8px 12px", textAlign: "left", width: "50px", color: "#64748b" }}>Item</th>
                          <th style={{ padding: "8px 12px", textAlign: "left", color: "#64748b" }}>Parameter Name</th>
                          <th style={{ padding: "8px 12px", textAlign: "left", width: "200px", color: "#64748b" }}>UOM</th>
                          <th style={{ padding: "8px 12px", textAlign: "center", width: "60px", color: "#64748b" }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {parameters.map((param, index) => (
                          <tr key={index} style={{ borderBottom: "1px solid #f1f5f9" }}>
                            <td style={{ padding: "10px 12px", fontWeight: 600, color: "#94a3b8" }}>{index + 1}.</td>
                            <td style={{ padding: "10px 12px" }}>
                              <input
                                type="text"
                                placeholder="Enter parameter name"
                                style={{ ...smallInput, height: "36px", marginBottom: 0 }}
                                value={param.name}
                                onChange={(e) => updateParameter(index, "name", e.target.value)}
                              />
                            </td>
                            <td style={{ padding: "10px 12px" }}>
                              <Select
                                options={uomOptions}
                                value={uomOptions.find(opt => opt.value === param.uom) || null}
                                onChange={(selected) => updateParameter(index, "uom", selected ? selected.value : "")}
                                placeholder="Select UOM"
                                isClearable
                                menuPortalTarget={document.body}
                                menuPosition={'fixed'}
                                styles={{
                                  control: (base) => ({ ...base, minHeight: 36, fontSize: 13 }),
                                  menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                }}
                              />
                            </td>
                            <td style={{ padding: "10px 12px", textAlign: "center" }}>
                              <button onClick={() => removeParameter(index)} style={{ background: "transparent", color: "#dc3545", border: "none", cursor: "pointer", fontSize: 15 }} title="Delete Parameter"><FaTrashAlt /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}


                  {activeTab === "changeLog" && (
                    <div style={{ background: "#fff", borderRadius: 6, overflow: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                            <th style={thStyle}>Date & Time</th>
                            <th style={thStyle}>User</th>
                            <th style={thStyle}>Action</th>
                            <th style={thStyle}>Field</th>
                            <th style={thStyle}>Old Value</th>
                            <th style={thStyle}>New Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {routingLogs.length > 0 ? routingLogs.map((log) => (
                            <tr key={log.id}>
                              <td style={tdStyle}>{log.created_at ? new Date(log.created_at).toLocaleString() : "-"}</td>
                              <td style={tdStyle}>{log.created_by || "-"}</td>
                              <td style={tdStyle}>{log.action_type || "-"}</td>
                              <td style={tdStyle}>{log.field_name || "-"}</td>
                              <td style={tdStyle}>{log.old_value || "-"}</td>
                              <td style={{ ...tdStyle, fontWeight: 700 }}>{log.new_value || "-"}</td>
                            </tr>
                          )) : <tr><td colSpan="6" style={{ textAlign: "center", padding: 20 }}>No change logs available</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>


              {/* Modal Footer */}
              <div style={{ padding: "16px 24px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 12, background: "white", borderRadius: "0 0 8px 8px" }}>
                <button onClick={() => setShowModal(false)} style={{ padding: "8px 16px", background: "#f1f5f9", border: "1px solid #cbd5e1", color: "#475569", borderRadius: 4, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Cancel</button>
                <button onClick={handleSave} style={{ padding: "8px 20px", background: "#28a745", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                  {editingId === "preview" ? "Save Draft" : editingId ? "Update Configuration" : "Save Configuration"}
                </button>
              </div>


            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default RoutingConfig;